import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { areaAPI } from '../../services/api';
import ErrorModal from '../Common/ErrorModal';
import { AdminPage, StateBlock } from '../Common/admin';

const AreaView = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadArea = useCallback(async () => {
    try {
      setLoading(true);
      const response = await areaAPI.getById(id);
      setRecord(response.data);
    } catch (err) {
      setErrorMessage(t('area.view.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadArea();
  }, [loadArea]);

  const headerActions = (
    <div className="ad-toolbar-group">
      <button type="button" className="ad-btn" onClick={() => navigate('/area')}>
        <i className="fas fa-arrow-right"></i>
        {t('area.view.backToList')}
      </button>
      <button
        type="button"
        className="ad-btn ad-btn-primary"
        onClick={() => navigate(`/area/edit/${id}`)}
      >
        <i className="fas fa-pen"></i>
        {t('area.view.edit')}
      </button>
    </div>
  );

  if (loading) {
    return (
      <AdminPage title={t('area.view.title')}>
        <StateBlock icon="fa-hourglass-half" title={t('common.loading')} />
      </AdminPage>
    );
  }

  if (!record) {
    return (
      <AdminPage title={t('area.view.title')}>
        <StateBlock icon="fa-circle-exclamation" title={t('area.list.messages.noData')} />
        <ErrorModal
          show={showErrorModal}
          message={errorMessage}
          onClose={() => setShowErrorModal(false)}
        />
      </AdminPage>
    );
  }

  const items = [
    { label: t('area.view.fields.areaCode'), value: record.areaCode },
    { label: t('area.view.fields.nameAr'), value: record.areaNameAr },
    { label: t('area.view.fields.nameEng'), value: record.areaNameEng },
    { label: t('area.view.fields.country'), value: record.country?.nameEng },
  ];

  return (
    <AdminPage
      title={record.areaNameAr || t('area.view.title')}
      subtitle={record.areaNameEng}
      headerActions={headerActions}
    >
      <div className="ad-detail">
        <div className="ad-detail-grid">
          {items.map((item) => (
            <div key={item.label} className="ad-detail-item">
              <span className="ad-detail-label">{item.label}</span>
              <span className="ad-detail-value">
                {item.value === null || item.value === undefined || item.value === ''
                  ? '-'
                  : item.value}
              </span>
            </div>
          ))}

          <div className="ad-detail-item">
            <span className="ad-detail-label">{t('area.view.fields.isActive')}</span>
            <span>
              <span
                className={`ad-badge ${record.isActive ? 'ad-badge-success' : 'ad-badge-danger'}`}
              >
                {record.isActive ? t('common.active') : t('common.inactive')}
              </span>
            </span>
          </div>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </AdminPage>
  );
};

export default AreaView;
